import { useState, useEffect } from 'react';
import { showOfflineBanner, hideOfflineBanner } from '@/lib/toast';

/**
 * Hook to track online/offline status
 *
 * Uses navigator.onLine + event listeners for real-time updates.
 * Shows a persistent banner when offline.
 *
 * @returns {{ isOnline: boolean }} Current online status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      hideOfflineBanner();
    };

    const handleOffline = () => {
      setIsOnline(false);
      showOfflineBanner();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Show banner if already offline on mount
    if (!navigator.onLine) {
      showOfflineBanner();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      hideOfflineBanner();
    };
  }, []);

  return { isOnline };
}
